<template>
    <div class="viewport" v-on:wheel="handleWheel">
        <!-- ** SOCIAL LINKS ** -->
        <div class="viewport__social-links"><social-icons></social-icons></div>

        <!-- ** BUTTON LOGO ** -->
        <div class="viewport__button viewport__button--logo"><button-logo></button-logo></div>

        <!-- ** BUTTON MENU ** -->
        <div class="viewport__button viewport__button--menu"><button-menu></button-menu></div>

        <!-- ** SLOT ** -->
        <div class="viewport__slot" v-on:touchstart="handleTouchStart" v-on:touchend="handleTouchEnd"><slot></slot></div>

        <!-- ** PROGRESS BAR ** -->
        <div class="viewport__progressbar" v-if="progressBarTotalSteps > 0">
            <progress-bar :progress="progressBarStep" :total-steps="progressBarTotalSteps"></progress-bar>
        </div>

        <!--** FOOTER **-->
        <div class="viewport__footer">
            <footer-component :enable-button="shouldScroll" :with-button="progressBarTotalSteps > 0"></footer-component>
        </div>
    </div>
</template>

<script>
import FooterComponent from '@/components/elements/Footer'
import SocialIcons from '@/components/elements/SocialIcons'
import ButtonLogo from '@/components/elements/buttons/ButtonLogo'
import ButtonMenu from '@/components/elements/buttons/ButtonMenu/ButtonMenu'
import ProgressBar from '@/components/elements/ProgressBar'

/**
 * Returns true if the user is touching the device screen
 * in order to enable the vertical scroll.
 *
 * @param startY the position of the tap on touchstart
 * @param endY the position of the tap on touchend
 *
 * @returns if true, the user wants to enable the vertical scroll
 */
function isVerticalScrolling (startY, endY) {
    const DELTA_Y = 100

    return endY < startY - DELTA_Y || endY > startY + DELTA_Y
}

export default {
    name: 'ViewPort',
    components: {
        ButtonLogo,
        ButtonMenu,
        FooterComponent,
        ProgressBar,
        SocialIcons,
    },
    data: () => ({
        currentRoutePath: '/',
        isTouchMoved: false,
        initialClientY: 0,
        shouldScroll: true,
    }),
    computed: {
        /**
         * It is for the effective progess of the progressbar
         */
        progressBarStep: {
            get () {
                return this.$store.state.slide.progress
            },
            set (newValue) {
                return newValue
            }
        },
        /**
         * It is for the numer of total records which compose the progressbar
         */
        progressBarTotalSteps: {
            get () {
                return this.$store.state.slide.totalSteps
            },
            set (newValue) {
                return newValue
            }
        },
    },
    mounted: function () {
        const homepageSlides = this.$store.state.homePage.slides

        this.$store.commit('slide/reset', homepageSlides.length - 1)
    },
    updated: function () {
        // if path changes
        if(this.currentRoutePath !== this.$router.history.current.path){
            const currentRoute = this.$router.history.current.name
            const pageState = this.$store.state[currentRoute] || ''
            const pageSlides = pageState && pageState.hasProgressBar ? pageState.slides.length - 1 : 0

            this.$store.commit('slide/reset', pageSlides)

            this.currentRoutePath = this.$router.history.current.path
        }
    },
    methods: {
        /**
         * When the user cheases to touch the mobile screen,
         * it checks if there was a vertical mobile scroll
         * and then brings to the next or the previous slide
         *
         * @param event touchend event
         */
        handleTouchEnd: function(event){

            const { changedTouches } = event
            const { clientY } = changedTouches[0]

            if (isVerticalScrolling(this.initialClientY, clientY)) {
                const mutation = clientY < this.initialClientY ? 'next' : 'prev'
                this.$store.commit(`${this.$route.name}/${mutation}`)
            }
        },

        /**
         * Detect the Y position of the screen
         * when the user starts to touch it
         *
         * @param event touchstart event
         */
        handleTouchStart: function(event){
            const { changedTouches } = event
            const { clientY } = changedTouches[0]

            this.initialClientY = clientY
        },

        /**
         * When the mouse wheels, brings to the next or the previous slide
         * and update the progressbar
         *
         * @param event mousewheel event
         */
        handleWheel: function(event){
            const slideMutation = event.deltaY > 0 ? 'next' : 'prev'
            const hasNextSlide = slideMutation === 'next' && this.$store.state.slide.totalSteps !== this.$store.state.slide.progress
                                 || slideMutation === 'prev' && this.$store.state.slide.progress > 0

            if (this.shouldScroll && hasNextSlide) {
                this.$store.commit(`${this.$route.name}/${slideMutation}`)
                this.shouldScroll = false
                const mutation = event.deltaY > 0 ? 'slide/increment' : 'slide/decrement'
                this.$store.commit(mutation)

                /*  Sets a timeout (the same time of animations in progressbar)
                    in order to avoid some unwanted behaviours
                    in case of strong wheel or extra wheel at the same time
                */
                setTimeout(() => {
                    this.shouldScroll = true
                }, 1301);
            }
        }
    }
}

</script>

<style lang="scss">
    @import './viewport.scss';
</style>
